/**
 * Value Piping Engine
 *
 * Allows referencing field values in labels, help text, placeholders, and options.
 * Syntax: {{field_name}} or {{field_id}}
 *
 * Examples:
 * - "Hello {{name}}, please confirm your email"
 * - "You selected {{product}}. What color would you like?"
 */

import type { FormField, FieldOption } from '../types/form';

/**
 * Process a string and replace piped values
 */
export function processPipedValue(
  template: string | undefined,
  values: Record<string, unknown>,
  fields: FormField[]
): string {
  if (!template) return '';

  return template.replace(/\{\{([^}]+)\}\}/g, (match, fieldRef) => {
    const trimmedRef = fieldRef.trim();

    // Try to find field by name first, then by id
    let field = fields.find(f => f.name === trimmedRef);
    if (!field) {
      field = fields.find(f => f.id === trimmedRef);
    }

    if (!field) {
      return match; // Keep original if field not found
    }

    const value = values[field.name];
    return formatPipedValue(value, field);
  });
}

/**
 * Format a value for display in piped text
 */
function formatPipedValue(value: unknown, field: FormField): string {
  if (value === null || value === undefined || value === '') {
    return '___'; // Placeholder for empty values
  }

  // Arrays (checkbox, multi-select)
  if (Array.isArray(value)) {
    const labels = value.map(v => {
      // Try to find the label from options
      const option = field.options?.find(opt => opt.value === v);
      return option?.label || v;
    });
    return labels.join(', ');
  }

  // For select/radio, try to get the label
  if (['select', 'radio'].includes(field.type) && field.options) {
    const option = field.options.find(opt => opt.value === value);
    if (option) {
      return option.label;
    }
  }

  // Rating - show as stars or number
  if (field.type === 'rating') {
    const num = Number(value);
    const icon = field.properties.ratingIcon === 'heart' ? '♥' : '★';
    return icon.repeat(num);
  }

  // Date formatting
  if (field.type === 'date' && typeof value === 'string') {
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return String(value);
    }
  }

  // DateTime formatting
  if (field.type === 'datetime' && typeof value === 'string') {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Process a field's display properties with piped values
 */
export function processFieldWithPiping(
  field: FormField,
  values: Record<string, unknown>,
  allFields: FormField[]
): { label: string; placeholder: string; helpText: string; options?: FieldOption[] } {
  return {
    label: processPipedValue(field.label, values, allFields),
    placeholder: processPipedValue(field.placeholder, values, allFields),
    helpText: processPipedValue(field.helpText, values, allFields),
    options: field.options?.map(opt => ({
      ...opt,
      label: processPipedValue(opt.label, values, allFields),
    })),
  };
}

/**
 * Check if a string contains piping syntax
 */
export function containsPiping(text: string | undefined): boolean {
  if (!text) return false;
  return /\{\{[^}]+\}\}/.test(text);
}

/**
 * Extract field references from a piped string
 */
export function extractPipedFields(text: string | undefined): string[] {
  if (!text) return [];

  const matches = text.matchAll(/\{\{([^}]+)\}\}/g);
  const refs: string[] = [];

  for (const match of matches) {
    refs.push(match[1].trim());
  }

  return [...new Set(refs)]; // Unique values
}

/**
 * Validate piped references in a string
 */
export function validatePipedReferences(
  text: string | undefined,
  fields: FormField[]
): { valid: boolean; invalidRefs: string[] } {
  const refs = extractPipedFields(text);
  const invalidRefs: string[] = [];

  for (const ref of refs) {
    const field = fields.find(f => f.name === ref || f.id === ref);
    if (!field) {
      invalidRefs.push(ref);
    }
  }

  return {
    valid: invalidRefs.length === 0,
    invalidRefs,
  };
}
